/**
 * vCard (VCF) generation utility — FE-007 fix.
 *
 * Generates a vCard 3.0 format string from a VirtualCardProps profile
 * and triggers a browser download via Blob + URL.createObjectURL.
 *
 * Spec ref: RFC 2426 (vCard 3.0), SPEC-008 §3.1.1 (profile data).
 *
 * AUDIT3-SHARED-015 (SPEC-046 QA AUDIT R1 IA — BATCH-6): added RFC 2426
 * §5 line folding via `foldLine()` — vCard lines MUST be folded at 75
 * octets (UTF-8 bytes, NOT chars) and continued with `\r\n ` (CRLF +
 * space). Pre-fix, long NOTE/URL values emitted a single >75-byte line
 * which strict vCard parsers (e.g., Apple Contacts) rejected silently.
 *
 * AUDIT3-SHARED-014 (SPEC-046 QA AUDIT R1 IA — BATCH-6): `escapeVCard`
 * now normalizes `\r\n` → `\n` BEFORE escaping `\n` → `\n` literal so
 * Windows-style line endings don't leak through as raw CR characters
 * (which break the VCF structure on RFC-strict parsers).
 *
 * AUDIT3-SHARED-013 (SPEC-046 QA AUDIT R1 IA — BATCH-6): the name is
 * now trimmed ONCE at the top of `generateVCard` and reused everywhere
 * (FN field + N field splitting). Pre-fix, FN used the untrimmed name
 * while N used the trimmed name — an inconsistency that produced
 * slightly different display vs. structured-name values for the same
 * profile.
 *
 * AUDIT3-SHARED-016 (SPEC-046 QA AUDIT R1 IA — BATCH-6): profile URL
 * now uses `window.location.origin` (browser) or
 * `process.env.NEXT_PUBLIC_SITE_URL` (SSR) instead of the hardcoded
 * `https://profileia.com` — fixes dev/staging/PWA-offline URLs.
 *
 * AUDIT3-SHARED-017 (SPEC-046 QA AUDIT R1 IA — BATCH-6): `X-SOCIALPROFILE`
 * is acknowledged as a non-standard vCard 3.0 extension (NOT in
 * RFC 2426). It is widely supported by Apple Contacts, Google Contacts,
 * and Thunderbird but may be ignored by stricter parsers. The standard
 * alternative would be `URL` per social platform, but `X-SOCIALPROFILE`
 * carries the platform TYPE (facebook, twitter, ...) which the standard
 * URL field cannot express.
 */

import type { VirtualCardProps } from '@/components/profile/virtual-card';

/**
 * Fold a single vCard content line per RFC 2426 §5.
 *
 * vCard lines MUST be at most 75 octets (UTF-8 bytes, NOT chars). Longer
 * lines are folded by splitting at 75-byte boundaries and joining the
 * continuations with `\r\n ` (CRLF + a single space — the space is the
 * RFC 2426 §5 "linear-whitespace" continuation marker).
 *
 * Splitting by UTF-8 BYTES (not chars) is critical — a multi-byte UTF-8
 * char (e.g., a 2-byte é) MUST NOT be split across a fold boundary
 * (that would produce invalid UTF-8 in the middle of the line). We
 * detect multi-byte sequences by their leading byte's high bits and
 * walk back to the previous safe boundary if a fold would land mid-char.
 *
 * AUDIT3-SHARED-015 (SPEC-046 QA AUDIT R1 IA — BATCH-6).
 */
export function foldLine(line: string): string {
  // Fast path: lines that fit in 75 bytes don't need folding.
  const encoder = new TextEncoder();
  const decoder = new TextDecoder();
  const bytes = encoder.encode(line);
  const byteLength = bytes.length;
  if (byteLength <= 75) return line;

  const chunks: string[] = [];
  let offset = 0;
  while (offset < bytes.length) {
    // Walk forward up to 75 bytes from `offset`, then walk back if we
    // landed in the middle of a multi-byte UTF-8 sequence.
    let end = Math.min(offset + 75, bytes.length);
    // If `end` is not the last byte, walk back while `bytes[end]` is a
    // UTF-8 continuation byte (0b10xxxxxx = 0x80..0xBF). A continuation
    // byte in the leading position means we're mid-character.
    while (end < bytes.length && (bytes[end] & 0xc0) === 0x80) {
      end -= 1;
    }
    chunks.push(decoder.decode(bytes.subarray(offset, end)));
    offset = end;
  }
  // RFC 2426 §5: continuations are joined with CRLF + a single space.
  return chunks.join('\r\n ');
}

/**
 * Generates a vCard 3.0 (VCF) format string from the user's profile data.
 *
 * VCF format reference: https://tools.ietf.org/html/rfc2426
 *
 * Properties included:
 *   - BEGIN/END: vCard delimiters
 *   - VERSION: 3.0
 *   - N: name (structured)
 *   - FN: formatted name (display name)
 *   - ORG: organization (package name as affiliation)
 *   - URL: profile URL
 *   - NOTE: bio
 *   - ADR: location (as locality)
 *   - SOCIALPROFILE: social links (X-SOCIALPROFILE extension — see the
 *     module-level AUDIT3-SHARED-017 note above for non-standard status)
 */
export function generateVCard(profile: VirtualCardProps): string {
  // AUDIT3-SHARED-013: trim the name ONCE here and use `trimmedName`
  // everywhere (FN field + N field splitting). Pre-fix, FN used the
  // untrimmed `profile.name` while N used `profile.name.trim()` — an
  // inconsistency that produced slightly different display vs.
  // structured-name values for the same profile.
  const trimmedName = profile.name.trim();
  const lines: string[] = [];

  lines.push('BEGIN:VCARD');
  lines.push('VERSION:3.0');

  // Formatted name (display name)
  lines.push(`FN:${escapeVCard(trimmedName)}`);

  // Structured name (LastName;FirstName;MiddleName;Prefix;Suffix)
  // We split the name heuristically: last word = last name, rest = first name
  const nameParts = trimmedName.split(/\s+/);
  if (nameParts.length >= 2) {
    const lastName = nameParts[nameParts.length - 1];
    const firstName = nameParts.slice(0, -1).join(' ');
    lines.push(`N:${escapeVCard(lastName)};${escapeVCard(firstName)};;;`);
  } else {
    lines.push(`N:;${escapeVCard(trimmedName)};;;`);
  }

  // Organization / affiliation (package name)
  if (profile.packageName) {
    lines.push(`ORG:${escapeVCard(profile.packageName)}`);
  }

  // PR #1454 fix: TEL field — WhatsApp number as phone number.
  // Founder report: 'no está guardando el numero de whatsapp como numero
  // de telefono'. The X-SOCIALPROFILE field is ignored by most contact
  // apps — the TEL field is what they read for phone numbers.
  // Extract digits from whatsapp (might be raw number OR wa.me URL).
  const whatsappRaw = profile.social?.whatsapp ?? '';
  if (whatsappRaw) {
    const whatsappNumber = whatsappRaw
      .replace(/^https?:\/\/wa\.me\//, '')
      .replace(/[^\d+]/g, '');
    if (whatsappNumber) {
      lines.push(`TEL;TYPE=CELL:${escapeVCard(whatsappNumber)}`);
    }
  }
  // Also add phone field if available on the profile (some VirtualCardProps
  // may have a phone field in the future — defense-in-depth).
  const phoneRaw = (profile as { phone?: string }).phone ?? '';
  if (phoneRaw) {
    const phoneNumber = phoneRaw.replace(/[^\d+]/g, '');
    if (phoneNumber) {
      lines.push(`TEL;TYPE=WORK:${escapeVCard(phoneNumber)}`);
    }
  }

  // Profile URL
  // BUG-EXP5-027 (SPEC-041 QA audit pre-launch): escape the profileUrl
  // via `escapeVCard()` for defense-in-depth. The username is validated
  // server-side by a strict regex (`[a-zA-Z0-9_]+`), so the URL should
  // never contain VCard-special chars (`,`, `;`, `\n`, `\`), but if the
  // backend validation ever loosens, an unescaped URL would break the
  // VCF structure (a `,` in the URL would terminate the URL field early
  // and inject garbage into the next property).
  //
  // AUDIT3-SHARED-016: use `window.location.origin` (browser) or
  // `process.env.NEXT_PUBLIC_SITE_URL` (SSR/Edge) instead of the
  // hardcoded `https://profileia.com` — fixes dev/staging/PWA-offline
  // URLs. The fallback `https://profileia.com` is the production origin
  // used only when both runtime + env are absent (shouldn't happen).
  const siteOrigin = typeof window !== 'undefined'
    ? window.location.origin
    : (process.env.NEXT_PUBLIC_SITE_URL ?? 'https://profileia.com');
  // SPEC-095 (P-146 fix): vCard URL field uses canonical /{username}
  const profileUrl = `${siteOrigin}/${profile.username}`;
  lines.push(`URL:${escapeVCard(profileUrl)}`);

  // Bio / note
  if (profile.bio) {
    lines.push(`NOTE:${escapeVCard(profile.bio)}`);
  }

  // Location
  if (profile.location) {
    // ADR: POBox;ExtendedAddress;Street;Locality;Region;PostalCode;Country
    lines.push(`ADR:;;${escapeVCard(profile.location)};;;;`);
  }

  // Social profiles (X-SOCIALPROFILE is a common vCard extension —
  // see AUDIT3-SHARED-017 module-level note on its non-standard status.)
  const socialEntries = Object.entries(profile.social).filter(([, url]) => url);
  for (const [platform, url] of socialEntries) {
    lines.push(`X-SOCIALPROFILE;TYPE=${escapeVCard(platform)}:${escapeVCard(url)}`);
  }

  lines.push('END:VCARD');

  // AUDIT3-SHARED-015: apply RFC 2426 §5 line folding to every line so
  // long values (NOTE bio, URL with long usernames, etc.) don't exceed
  // the 75-octet limit. Pre-fix, strict parsers (Apple Contacts) would
  // silently reject lines > 75 bytes.
  return lines.map(foldLine).join('\r\n');
}

/**
 * Escapes special characters in vCard values per RFC 2426:
 *   - Backslash → \\
 *   - Comma → \,
 *   - Semicolon → \;
 *   - Newline → \n (literal backslash-n in the VCF output)
 *
 * AUDIT3-SHARED-014 (SPEC-046 QA AUDIT R1 IA — BATCH-6): normalize
 * `\r\n` → `\n` and `\r` → `\n` BEFORE escaping `\n` → `\n` literal.
 * Pre-fix, Windows-style CRLF line endings leaked through as raw `\r`
 * characters which broke the VCF structure on RFC-strict parsers (the
 * raw `\r` is not a vCard content-line delimiter — only `\r\n` is).
 * The order matters: if we escaped `\n` first, the `\r` from a `\r\n`
 * pair would be left orphaned with no `\n` to pair with.
 */
export function escapeVCard(value: string): string {
  return value
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\\/g, '\\\\')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;')
    .replace(/\n/g, '\\n');
}

/**
 * Triggers a browser download of a vCard file generated from the profile.
 *
 * Uses Blob + URL.createObjectURL to create a temporary download link,
 * then revokes the object URL after the download starts.
 */
export function downloadVCard(profile: VirtualCardProps): void {
  const vcfContent = generateVCard(profile);
  const blob = new Blob([vcfContent], { type: 'text/vcard;charset=utf-8' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = `${profile.username}.vcf`;
  document.body.appendChild(link);
  link.click();

  // Cleanup
  document.body.removeChild(link);
  // C-08 fix: delay revocation by 5s to avoid Firefox silent download
  // failure (Firefox's download is async and revoking the URL too early
  // causes the download to fail silently).
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}
